import { LocationMarkerIcon } from "@heroicons/react/outline";
import { isEmpty } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import DisplayLabels from "../../../components/labels/DisplayLabels";
import { useAuthentication } from "../../../context/authentication";
import { useContacts } from "../../../context/contacts";
import { useLocations } from "../../../context/locations";
import { formatDateToLocale } from "../../../utils/dates";
import { HotspotLocation } from "../../../utils/types";
import MemberCell from "../../Workspace/common/MemberCell";
import ContactFormSidebar from "../../Workspace/ContactsData/ContactFormSidebar";

const ContactLocationsTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const { wrapActionWithAuth } = useAuthentication();
  const { contacts, currentContact } = useContacts();
  const { locations, setEditLocationConfig } = useLocations();
  const [openContactFormSidebar, setOpenContactFormSidebar] = useState(false);

  const contact = useMemo(
    () =>
      currentContact ||
      (props.match.params.id ? contacts[props.match.params.id] : undefined),
    [contacts, currentContact, props.match.params.id]
  );

  const renderLocationRow = useCallback(
    (locationUuid: string) => {
      const location = locations[locationUuid];

      if (!location) return null;

      return (
        <tr key={locationUuid}>
          <td className="px-6 py-4 whitespace-nowrap">
            <Link
              className="relative flex items-center space-x-3 group"
              to={`/locations/${locationUuid}`}
            >
              <div className="flex-shrink-0 flex justify-center items-center w-10">
                <LocationMarkerIcon className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0 focus:outline-none">
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                  {location.name}
                </p>
                <DisplayLabels linkedUuid={location.uuid} size="small" />
              </div>
            </Link>
          </td>

          <td className="px-6 py-4 whitespace-nowrap">
            <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
              {location.address}
            </p>
          </td>

          <td className="px-6 py-4 whitespace-nowrap">
            <MemberCell
              memberUuid={location.created_by}
              subtitle={formatDateToLocale(location.created_at)}
            />
          </td>
        </tr>
      );
    },
    [locations]
  );

  const emptyLocation: HotspotLocation = useMemo(
    () => ({
      address: "",
      created_at: "",
      created_by: "",
      h3_index_r12: "",
      h3_index_r4: "",
      height: 0,
      lat: 0,
      linked_contact_ids: contact?.uuid ? [contact.uuid] : [],
      lng: 0,
      metadata: null,
      name: "",
      updated_at: "",
      updated_by: "",
      uuid: "",
      super_simulations: [],
      comments: []
    }),
    [contact?.uuid]
  );

  if (!contact) return null;

  return (
    <>
      <ContactFormSidebar
        contact={contact}
        open={openContactFormSidebar}
        toggleEditContactForm={() =>
          setOpenContactFormSidebar(!openContactFormSidebar)
        }
        showLinkedLocations={true}
      />

      <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="w-full py-2 align-middle inline-block min-w-full">
          {!isEmpty(contact.linked_location_ids) ? (
            <div className="shadow w-full overflow-x-auto sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-500">
                <thead className="bg-gray-50 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      <div className="flex items-center">
                        <span className="mr-1">
                          {contact.linked_location_ids.length} linked location
                          {contact.linked_location_ids.length > 1 ? "s" : ""}
                        </span>
                      </div>
                    </th>

                    <th
                      scope="col"
                      style={{ minWidth: "9rem" }}
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Address
                    </th>

                    <th
                      scope="col"
                      style={{ minWidth: "9rem" }}
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Added
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
                  {contact.linked_location_ids.map(renderLocationRow)}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex">
              <p className="text-sm text-gray-500 dark:text-gray-300">
                This contact has no linked locations yet. Locations can be
                linked from the{" "}
                <b
                  onClick={wrapActionWithAuth(() =>
                    setOpenContactFormSidebar(true)
                  )}
                  className="cursor-pointer hover:underline"
                >
                  contact form
                </b>{" "}
                and from the{" "}
                <b
                  onClick={wrapActionWithAuth(() =>
                    setEditLocationConfig({
                      show: true,
                      location: emptyLocation
                    })
                  )}
                  className="cursor-pointer hover:underline"
                >
                  location form
                </b>
                .
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ContactLocationsTab;
